/**
 * Global console message storage and state management.
 * This module handles the core console message system that the AI tool reads from.
 */

export interface ConsoleMessage {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  timestamp: number;
}

export type ErrorSeverity = 'critical' | 'warning' | 'info';

export interface ErrorCategory {
  id: string;
  name: string;
  severity: ErrorSeverity;
  icon: string;
}

export interface ParsedError {
  original: ConsoleMessage;
  category: ErrorCategory;
  plainEnglish: string;
  technicalDetails: string;
  possibleCause: string;
  suggestedFix: string;
}

export interface GroupedError {
  key: string;
  errors: ParsedError[];
  count: number;
  firstSeen: number;
  lastSeen: number;
}

/**
 * Error categories for grouping
 */
export const ERROR_CATEGORIES: Record<string, ErrorCategory> = {
  syntax: { id: 'syntax', name: 'Syntax Error', severity: 'critical', icon: '🔴' },
  reference: { id: 'reference', name: 'Missing Reference', severity: 'critical', icon: '🔴' },
  type: { id: 'type', name: 'Type Error', severity: 'critical', icon: '🔴' },
  network: { id: 'network', name: 'Network Issue', severity: 'warning', icon: '🟡' },
  cors: { id: 'cors', name: 'Cross-Origin Block', severity: 'warning', icon: '🟡' },
  react: { id: 'react', name: 'React Error', severity: 'critical', icon: '🔴' },
  deprecation: { id: 'deprecation', name: 'Deprecation Warning', severity: 'info', icon: '🔵' },
  security: { id: 'security', name: 'Security Warning', severity: 'warning', icon: '🟡' },
  resource: { id: 'resource', name: 'Resource Not Found', severity: 'warning', icon: '🟡' },
  unknown: { id: 'unknown', name: 'Other', severity: 'info', icon: '⚪' },
};

/**
 * Pattern matching for error categorization and plain English translation
 */
interface ErrorPattern {
  pattern: RegExp;
  category: string;
  plainEnglish: (match: RegExpMatchArray, message: string) => string;
  possibleCause: string;
  suggestedFix: string;
}

const ERROR_PATTERNS: ErrorPattern[] = [
  // TypeError patterns
  {
    pattern: /TypeError: Cannot read propert(?:y|ies) (?:of |'[^']+' of )?(?:undefined|null)/i,
    category: 'type',
    plainEnglish: () => "Something is missing. The code tried to use data that doesn't exist yet.",
    possibleCause: "A variable or object property is undefined when the code tries to access it.",
    suggestedFix: "Add a check to make sure the data exists before using it, or provide a default value.",
  },
  {
    pattern: /TypeError: (\w+) is not a function/i,
    category: 'type',
    plainEnglish: (match) => `"${match[1]}" was expected to be a function, but it's not.`,
    possibleCause: "You're trying to call something that isn't a function, possibly due to a typo or incorrect import.",
    suggestedFix: "Check the spelling and make sure the function is properly imported or defined.",
  },
  {
    pattern: /TypeError: Cannot set propert(?:y|ies)/i,
    category: 'type',
    plainEnglish: () => "The code tried to change something that can't be changed.",
    possibleCause: "Attempting to modify a read-only property or an object that doesn't exist.",
    suggestedFix: "Make sure the object exists and the property is writable.",
  },
  
  // ReferenceError patterns
  {
    pattern: /ReferenceError: (\w+) is not defined/i,
    category: 'reference',
    plainEnglish: (match) => `The code uses "${match[1]}" but it hasn't been created yet.`,
    possibleCause: "A variable or function is used before it's declared, or there's a typo in the name.",
    suggestedFix: "Check spelling and make sure it's imported or defined before use.",
  },
  
  // SyntaxError patterns
  {
    pattern: /SyntaxError: Unexpected token/i,
    category: 'syntax',
    plainEnglish: () => "There's a typo in the code. Something is written incorrectly.",
    possibleCause: "Missing or extra brackets, commas, quotes, or other syntax characters.",
    suggestedFix: "Look for missing commas, brackets, or quotes near the error location.",
  },
  {
    pattern: /SyntaxError: Unexpected end of/i,
    category: 'syntax',
    plainEnglish: () => "The code ended unexpectedly. Something is incomplete.",
    possibleCause: "A bracket, quote, or statement wasn't properly closed.",
    suggestedFix: "Check for missing closing brackets } ] ) or quotes.",
  },
  {
    pattern: /SyntaxError:/i,
    category: 'syntax',
    plainEnglish: () => "There's a typo or formatting error in the code.",
    possibleCause: "Invalid JavaScript syntax.",
    suggestedFix: "Review the code for syntax mistakes.",
  },
  
  // Network/CORS patterns
  {
    pattern: /CORS|Cross-Origin|Access-Control-Allow-Origin/i,
    category: 'cors',
    plainEnglish: () => "The app tried to load data from another website that blocked the request.",
    possibleCause: "The external server doesn't allow requests from your app's domain.",
    suggestedFix: "Use a CORS proxy, or ask the API provider to allow your domain.",
  },
  {
    pattern: /Failed to fetch|NetworkError|net::ERR_|fetch failed/i,
    category: 'network',
    plainEnglish: () => "Couldn't connect to the server. The network request failed.",
    possibleCause: "The server might be down, URL might be wrong, or there's no internet connection.",
    suggestedFix: "Check the URL, your internet connection, and if the server is running.",
  },
  {
    pattern: /404|Not Found/i,
    category: 'resource',
    plainEnglish: () => "A file or page couldn't be found.",
    possibleCause: "The URL is incorrect or the resource was moved/deleted.",
    suggestedFix: "Double-check the URL or file path.",
  },
  {
    pattern: /500|Internal Server Error/i,
    category: 'network',
    plainEnglish: () => "The server had an internal error.",
    possibleCause: "Something went wrong on the server side.",
    suggestedFix: "This is usually a server issue. Try again later or check server logs.",
  },
  
  // React patterns
  {
    pattern: /Maximum update depth exceeded|too many re-renders/i,
    category: 'react',
    plainEnglish: () => "The app got stuck in an infinite loop of updates.",
    possibleCause: "A component keeps triggering updates endlessly, usually from useEffect or state updates.",
    suggestedFix: "Check useEffect dependencies and avoid setting state that causes immediate re-renders.",
  },
  {
    pattern: /Invalid hook call/i,
    category: 'react',
    plainEnglish: () => "A React hook is being used incorrectly.",
    possibleCause: "Hooks can only be used inside React components, at the top level.",
    suggestedFix: "Move the hook call to the top level of your component function.",
  },
  {
    pattern: /React|component|render|JSX|hook|useState|useEffect/i,
    category: 'react',
    plainEnglish: () => "Something went wrong with a React component.",
    possibleCause: "An error occurred during rendering or in a React hook.",
    suggestedFix: "Check the component's code and its props/state.",
  },
  {
    pattern: /Hydration|server .* client|mismatch/i,
    category: 'react',
    plainEnglish: () => "The server and browser versions of the page don't match.",
    possibleCause: "The HTML from the server differs from what React generates in the browser.",
    suggestedFix: "Ensure server and client render the same content initially.",
  },
  
  // Deprecation patterns
  {
    pattern: /deprecated|will be removed/i,
    category: 'deprecation',
    plainEnglish: () => "This feature is outdated and may stop working in the future.",
    possibleCause: "Using an old API or method that has a newer replacement.",
    suggestedFix: "Update to the recommended alternative mentioned in the warning.",
  },
  
  // Security patterns
  {
    pattern: /security|unsafe|XSS|injection|CSP|Content-Security-Policy/i,
    category: 'security',
    plainEnglish: () => "There's a potential security concern.",
    possibleCause: "The code might be doing something that could be a security risk.",
    suggestedFix: "Review the security warning and follow best practices.",
  },
  
  // Module/Import patterns
  {
    pattern: /Cannot find module|Module not found|Failed to resolve/i,
    category: 'reference',
    plainEnglish: () => "A required file or package couldn't be found.",
    possibleCause: "The import path is wrong or the package isn't installed.",
    suggestedFix: "Check the import path and run npm install if needed.",
  },
];

/**
 * Parse a console message into a user-friendly format
 */
export function parseError(msg: ConsoleMessage): ParsedError {
  const message = msg.message;
  
  // Find matching pattern
  for (const errorPattern of ERROR_PATTERNS) {
    const match = message.match(errorPattern.pattern);
    if (match) {
      return {
        original: msg,
        category: ERROR_CATEGORIES[errorPattern.category],
        plainEnglish: errorPattern.plainEnglish(match, message),
        technicalDetails: message,
        possibleCause: errorPattern.possibleCause,
        suggestedFix: errorPattern.suggestedFix,
      };
    }
  }
  
  // Default fallback for unmatched errors
  const defaultCategory = msg.level === 'error' 
    ? ERROR_CATEGORIES.unknown 
    : msg.level === 'warn'
      ? { ...ERROR_CATEGORIES.unknown, severity: 'warning' as ErrorSeverity }
      : { ...ERROR_CATEGORIES.unknown, severity: 'info' as ErrorSeverity };
  
  return {
    original: msg,
    category: defaultCategory,
    plainEnglish: msg.level === 'error' 
      ? "An error occurred in your application."
      : msg.level === 'warn'
        ? "There's a warning you might want to check."
        : "Information from your application.",
    technicalDetails: message,
    possibleCause: "See technical details for more information.",
    suggestedFix: "Review the error message and stack trace for clues.",
  };
}

/**
 * Generate a grouping key for deduplication
 */
function getErrorGroupKey(parsed: ParsedError): string {
  // Group by category + first line of error (normalized)
  const firstLine = parsed.original.message.split('\n')[0].trim();
  // Remove variable parts like line numbers, specific values
  const normalized = firstLine
    .replace(/:\d+:\d+/g, ':X:X') // Line:col numbers
    .replace(/0x[0-9a-f]+/gi, '0xXXX') // Hex addresses
    .replace(/'[^']+'/g, "'...'") // Quoted strings
    .replace(/"[^"]+"/g, '"..."') // Double quoted strings
    .replace(/\d+/g, 'N'); // Numbers
  return `${parsed.category.id}:${normalized}`;
}

/**
 * Group and deduplicate errors
 */
export function groupErrors(messages: ConsoleMessage[]): GroupedError[] {
  const groups = new Map<string, GroupedError>();
  
  for (const msg of messages) {
    const parsed = parseError(msg);
    const key = getErrorGroupKey(parsed);
    
    const existing = groups.get(key);
    if (existing) {
      existing.errors.push(parsed);
      existing.count++;
      existing.lastSeen = msg.timestamp;
    } else {
      groups.set(key, {
        key,
        errors: [parsed],
        count: 1,
        firstSeen: msg.timestamp,
        lastSeen: msg.timestamp,
      });
    }
  }
  
  // Sort by last seen (most recent first)
  return Array.from(groups.values()).sort((a, b) => b.lastSeen - a.lastSeen);
}

/**
 * Get summary statistics for console messages
 */
export function getErrorSummary(messages: ConsoleMessage[]): {
  total: number;
  critical: number;
  warnings: number;
  info: number;
  categories: Record<string, number>;
} {
  const summary = {
    total: messages.length,
    critical: 0,
    warnings: 0,
    info: 0,
    categories: {} as Record<string, number>,
  };
  
  for (const msg of messages) {
    const parsed = parseError(msg);
    
    switch (parsed.category.severity) {
      case 'critical':
        summary.critical++;
        break;
      case 'warning':
        summary.warnings++;
        break;
      case 'info':
        summary.info++;
        break;
    }
    
    summary.categories[parsed.category.name] = (summary.categories[parsed.category.name] || 0) + 1;
  }
  
  return summary;
}

/**
 * Custom error class for project preview console errors
 */
export class ProjectPreviewConsoleError extends Error {
  public readonly logs: ConsoleMessage[];

  constructor(message: string, logs: ConsoleMessage[]) {
    super(message);
    this.name = 'ProjectPreviewConsoleError';
    this.logs = logs;
  }
}

// Global console messages storage
const consoleMessages: ConsoleMessage[] = [];
const listeners: Array<() => void> = [];

const notifyListeners = () => {
  listeners.forEach(listener => {
    try {
      listener();
    } catch (err) {
      console.warn('Console message listener failed:', err);
    }
  });
};

export const addConsoleMessage = (level: ConsoleMessage['level'], message: string) => {
  consoleMessages.push({ level, message, timestamp: Date.now() });
  notifyListeners();
};

export const addConsoleMessageListener = (listener: () => void) => {
  listeners.push(listener);
};

export const removeConsoleMessageListener = (listener: () => void) => {
  const index = listeners.indexOf(listener);
  if (index > -1) {
    listeners.splice(index, 1);
  }
};

export const getHasConsoleErrors = (): boolean => {
  return consoleMessages.some(msg => msg.level === 'error');
};

export const getConsoleMessages = (): ConsoleMessage[] => {
  return [...consoleMessages];
};

export const clearConsoleMessages = () => {
  consoleMessages.length = 0;
  notifyListeners();
};
